import React from 'react';
import { Modal, Button } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { numberFormatter } from '../../components/formatters/numberFormatter';

const PrintEndOfDayModal = ({ allTransactions, visible, onCancel }) => {

  if (!allTransactions || allTransactions.length === 0) {
    return (
      <Modal
        title="End of Day Report"
        open={visible}
        onCancel={onCancel}
        footer={[
          <Button key="close" onClick={onCancel}>Close</Button>,
        ]}
      >
        <p>No transactions found for the end of day report.</p>
      </Modal>
    );
  }

  const transactionsByCategory = {};
  let totalSales = 0;
  let totalReturns = 0;

  // Process all transactions regardless of session
  allTransactions?.forEach(transaction => {
    const transactionTotal = parseFloat(transaction.total || 0);
    if (transaction.isOut) totalReturns += transactionTotal;
    else totalSales += transactionTotal;

    transaction.cart?.forEach(cartItem => {
      const categoryName = cartItem?.category || 'Uncategorized';
      if (!transactionsByCategory[categoryName]) transactionsByCategory[categoryName] = {};

      const productName = cartItem?.name || 'Unknown Product';
      const transactionType = transaction.isCredit ? 'Credit' : (transaction.isOut ? 'Return' : 'Sale');
      
      const productKey = `${productName}_${transactionType}`;

      if (!transactionsByCategory[categoryName][productKey]) {
        transactionsByCategory[categoryName][productKey] = {
          ...cartItem,
          productName: productName,
          quantity: 0,
          total: 0,
          transactionDates: [],
          type: transactionType,
        };
      }

      const qty = Number(cartItem.quantity) || 0;
      const price = Number(cartItem.price) || 0;

      transactionsByCategory[categoryName][productKey].quantity += qty;
      transactionsByCategory[categoryName][productKey].total += qty * price;
      transactionsByCategory[categoryName][productKey].transactionDates.push(transaction.createdAt);
    });
  });

  const netTotal = totalSales - totalReturns;

  /**
   * Generates and prints a receipt-style HTML document for the end-of-day report.
   */
  const handlePrint = () => {
    let printContent = `
    <html>
      <head>
        <title>End of Day Report</title>
        <style>
          @media print {
            @page {
              size: 80mm auto; 
              margin: 3mm;
            }
            html, body {
              margin: 0;
              padding: 0;
              color: #000;
              font-size: 14px; 
              font-family: 'Courier New', Courier, monospace;
              height: auto;
              overflow: visible;
            }
          }
          .receipt-container {
            width: 100%;
            margin: 0 auto;
          }
          .header, .footer {
            text-align: center;
          }
          .header h2, .header p {
            margin: 3px 0;
          }
          .section-title {
            font-weight: bold;
            text-align: center;
            margin-bottom: 8px;
            font-size: 16px;
          }
          .summary-section {
            padding: 8px 0;
            margin-bottom: 15px;
            font-weight: bold; /* Makes the entire summary section bold */
          }
          .summary-item {
            display: flex;
            justify-content: space-between;
            margin-bottom: 4px;
          }
          .category-wrapper {
            margin-top: 15px;
          }
          .transaction-table {
            width: 100%;
            border-collapse: collapse; /* Crucial for clean borders */
            margin-top: 8px;
          }
          .transaction-table th, .transaction-table td {
            border: 1px solid #000; /* Adds borders to all table cells */
            padding: 4px;
            text-align: left;
          }
          .transaction-table th {
            font-weight: bold;
          }
          .transaction-table .col-right {
            text-align: right;
          }
          .transaction-table .category-total-row td {
            font-weight: bold;
          }
          .net-total {
            font-size: 18px;
            border-top: 1px solid #000;
            padding-top: 5px;
            margin-top: 5px;
          }
        </style>
      </head>
      <body>
        <div class="receipt-container">
    `;

    // Report Header
    printContent += `
        <header class="header">
          <h2>End of Day Report</h2>
        </header>
    `;

    // Summary Information
    printContent += `
        <section class="summary-section">
            <p><strong>Report Date:</strong> ${new Date().toLocaleDateString()}</p>
            <p><strong>Total Transactions:</strong> ${allTransactions.length}</p>
            <p><strong>Generated At:</strong> ${new Date().toLocaleString()}</p>
            <hr style="border: none; border-top: 1px dashed #000; margin: 10px 0;" />
            <div class="summary-item">
                <span>Total Sales:</span>
                <span>${numberFormatter(totalSales)}</span>
            </div>
            <div class="summary-item">
                <span>Total Returns:</span>
                <span>${numberFormatter(totalReturns)}</span>
            </div>
            <div class="summary-item net-total">
                <span>Net Total:</span>
                <span>${numberFormatter(netTotal)}</span>
            </div>
        </section>
    `;

    // Transactions by Category
    if (Object.keys(transactionsByCategory).length > 0) {
      Object.entries(transactionsByCategory).forEach(([category, products]) => {
        const categoryTotal = Object.values(products).reduce((sum, item) => sum + item.total, 0);

        printContent += `<div class="category-wrapper">`;
        printContent += `<h4 class="section-title">${category}</h4>`;

        printContent += `
            <table class="transaction-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th class="col-right">Qty</th>
                  <th class="col-right">Total</th>
                  <th class="col-right">Type</th>
                </tr>
              </thead>
              <tbody>
        `;

        Object.values(products).forEach(item => {
          printContent += `
            <tr>
              <td>${item.productName}</td>
              <td class="col-right">${item.quantity}</td>
              <td class="col-right">${numberFormatter(item.total)}</td>
              <td class="col-right">${item.type}</td>
            </tr>
          `;
        });
        
        printContent += `
            <tr class="category-total-row">
                <td colspan="2" class="col-right">${category} Total:</td>
                <td class="col-right">${numberFormatter(categoryTotal)}</td>
                <td></td>
            </tr>
          `;

        printContent += `</tbody></table></div>`;
      });
    } else {
      printContent += `<p style="text-align: center; padding: 10px 0;">No transactions found for this period.</p>`;
    }

    // Footer
    printContent += `
        <footer class="footer" style="margin-top: 20px;">
          
        </footer>
      </div>
    </body>
    </html>
    `;

    // Create a new window to print the content
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    } else {
        alert("Please allow pop-ups to print the report.");
    }
  };

  return (
    <Modal
      title="End of Day Report"
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="close" onClick={onCancel}>Close</Button>,
        <Button
          key="print"
          type="primary"
          icon={<PrinterOutlined />}
          onClick={handlePrint}
        >
          Print
        </Button>,
      ]}
      width={900}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}
      style={{ top: 20 }}
    >
      {/* The content displayed in the modal remains unchanged */}
      <div className="print-container">
        <div style={{ marginBottom: '20px', fontSize: '14px' }}>
          <h2>End of Day Report</h2>
          <div><strong>Report Date:</strong> {new Date().toLocaleDateString()}</div>
          <div><strong>Total Transactions:</strong> {allTransactions.length}</div>
          <div><strong>Generated At:</strong> {new Date().toLocaleString()}</div>
        </div>

        <div style={{ marginBottom: '20px', fontSize: '14px' }}>
          <div><strong>Total Sales:</strong> {numberFormatter(totalSales)}</div>
          <div><strong>Total Returns:</strong> {numberFormatter(totalReturns)}</div>
          <div><strong>Net Total:</strong> {numberFormatter(netTotal)}</div>
        </div>

        <div>
          {Object.keys(transactionsByCategory).length > 0 ? (
            Object.entries(transactionsByCategory).map(([category, products]) => {
              const categoryTotal = Object.values(products).reduce((sum, item) => sum + item.total, 0);
              
              return (
                <div key={category} style={{ marginBottom: '25px' }}>
                  <h4 style={{ padding: '8px', borderRadius: '4px', backgroundColor: '#f0f2f5' }}>{category}</h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Product</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Qty</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Total</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(products).map((item, index) => (
                        <tr key={index}>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            {item.productName}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>
                            {item.quantity}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                            {numberFormatter(item.total)}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>
                            {item.type}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }} colSpan="2">
                          {category} Total:
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                          {numberFormatter(categoryTotal)}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })
          ) : (
            <p>No transactions found for this period.</p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PrintEndOfDayModal;